import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetProjectionQueryKey,
  getGetReconciliationQueryKey,
  getListAnchorsQueryKey,
  useDeleteAnchor,
  useListAnchors,
  useSetAnchor,
} from '../api/generated/fluence'
import type { AccountLedger, BalanceAnchor } from '../api/generated/model'
import { formatDate, formatMoney, toMajor, toMinor } from '../lib/money'

/**
 * Correcting observed balances.
 *
 * The highest-leverage action in the app: a projection rolls forward from the most recent
 * anchor, so recording what the bank actually says re-bases everything after it and
 * discards accumulated modelling error in one stroke (charter 4.1).
 *
 * The form prefills with what the model currently believes, so the user types what their
 * bank shows and the difference is displayed. That difference is the drift being thrown
 * away, and showing it is the point — it is the only direct measure of how far the whole
 * model has wandered.
 */
export function AnchorEditor({ accounts }: { accounts: AccountLedger[] }) {
  const queryClient = useQueryClient()
  const [openFor, setOpenFor] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  const anchors = useListAnchors()

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListAnchorsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetProjectionQueryKey().slice(0, 1) }),
      queryClient.invalidateQueries({ queryKey: getGetReconciliationQueryKey() }),
    ])
  }
  const set = useSetAnchor({ mutation: { onSuccess: refresh } })
  const remove = useDeleteAnchor({ mutation: { onSuccess: refresh } })

  const list: BalanceAnchor[] = anchors.data?.status === 200 ? anchors.data.data : []
  const busy = set.isPending || remove.isPending

  function open(account: AccountLedger) {
    setError(null)
    setOpenFor(account.accountId)
    setDate(today())
    // Prefilled with the model's own belief for today, so the user is correcting a
    // number rather than producing one from nothing.
    setAmount(String(toMajor(account.openingBalanceMinor, account.currency)))
  }

  async function submit(account: AccountLedger) {
    const balanceMinor = toMinor(amount, account.currency)
    if (balanceMinor === null) {
      setError('Balance must be a number.')
      return
    }
    const response = await set.mutateAsync({
      data: {
        accountId: account.accountId,
        date,
        balanceMinor,
        currency: account.currency,
      },
    })
    if (response.status === 200) {
      setOpenFor(null)
      return
    }
    setError('The server rejected that.')
  }

  async function confirmRemove(anchor: BalanceAnchor) {
    setError(null)
    const response = await remove.mutateAsync({ id: anchor.id })
    if (response.status === 204) return
    // The server refuses to remove an account's last anchor, because a projection cannot
    // start without one. Surfacing its reasoning beats a generic failure.
    setError(
      typeof response.data === 'object' && response.data && 'detail' in response.data
        ? String((response.data as { detail: unknown }).detail)
        : 'That anchor could not be removed.',
    )
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Observed balances</h2>
        <span className="panel-note">what the bank says, not what the model guessed</span>
      </div>

      {error && <p className="cf-error">{error}</p>}

      <ul className="anchor-list">
        {accounts.map((account) => {
          const forAccount = list.filter((a) => a.accountId === account.accountId)
          const latest = forAccount[0]
          const isOpen = openFor === account.accountId
          const typed = isOpen ? toMinor(amount, account.currency) : null
          const delta = typed === null ? null : typed - account.openingBalanceMinor

          return (
            <li key={account.accountId}>
              <div className="anchor-row">
                <span className="anchor-name">{account.name}</span>
                <span className="anchor-latest">
                  {latest
                    ? `last confirmed ${formatDate(latest.date)} at ${formatMoney(latest.balanceMinor, latest.currency)}`
                    : 'never confirmed'}
                </span>
                {!isOpen && (
                  <button
                    type="button"
                    className="btn-ghost btn-small"
                    onClick={() => open(account)}
                    disabled={busy}
                  >
                    Correct
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="pending-form">
                  <label>
                    <span>As of</span>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </label>
                  <label>
                    <span>Actual balance ({account.currency})</span>
                    <input
                      value={amount}
                      inputMode="decimal"
                      autoFocus
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </label>
                  {delta !== null && delta !== 0 && (
                    <span className={`anchor-delta ${delta < 0 ? 'is-over' : 'is-under'}`}>
                      model is {delta < 0 ? 'over' : 'under'} by{' '}
                      {formatMoney(Math.abs(delta), account.currency)}
                    </span>
                  )}
                  <div className="cf-actions">
                    <button
                      type="button"
                      className="btn-ghost btn-small"
                      onClick={() => setOpenFor(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary btn-small"
                      onClick={() => submit(account)}
                      disabled={busy}
                    >
                      Confirm balance
                    </button>
                  </div>
                </div>
              )}

              {forAccount.length > 1 && (
                <details className="anchor-history">
                  <summary>{forAccount.length} confirmations</summary>
                  <ul>
                    {forAccount.map((anchor) => (
                      <li key={anchor.id}>
                        <span className="pending-date">{formatDate(anchor.date)}</span>
                        <span className="cf-amount">
                          {formatMoney(anchor.balanceMinor, anchor.currency)}
                        </span>
                        <button
                          type="button"
                          className="btn-ghost btn-small"
                          onClick={() => confirmRemove(anchor)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}
